//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';

import { getIsRevealInsightsFeatureEnabled } from '@commerce/selectors/getIsRevealInsightsFeatureEnabled';
import { getIsGalleriesEnabled } from '@galleries/selectors/getIsGalleriesEnabled';
import {
  getIsLike2BuyEnabled,
  getIsLike2BuyTiktokEnabled,
} from '@insights/redux/selectors/getAvailableInsightsPages';
import { getIsLike2BuyV2Enabled } from '@instagram-account/redux/like2buy/selectors/getIsLike2BuyV2Enabled';
import { NavDropdown } from '@legacy-components/nav3/navigation-menus/NavDropdown';
import { NavDropdownItem } from '@legacy-components/nav3/navigation-menus/NavDropdownItem';
import { NAV_DROPDOWN_ITEMS_TRACKING_ID } from '@legacy-components/nav3/navigation-menus/constants/NavMenuItemConstants';
import { getIsGoogleAssetFeedsEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsAssetFeedTaggingEnabled';
import { getIsEmailBuilderEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsEmailBuilderEnabled';
import { getIsInstagramShopsUgcEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsInstagramShopsUgcEnabled';
import { getIsInstashopFeatureEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsInstashopEnabled';
import { getIsTributarySelfServeEnabled } from '@media-library/redux/getMediaLibraryPage';
import { getIsPromotedPostsAccessEnabled } from '@publishing/analytics/selectors/getAnalyticsFeaturesEnabled';
import {
  getIsInstagramAdsContentAllowlistingEnabled,
  getIsTiktokSparkAdsEnabled,
} from '@redux/selectors/getIsPaidSocialFeatureEnabled';
import { getIsCuralateEmployee } from '@redux/selectors/getSessionInfo';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';

import { hasItemAccessByStateName } from 'app/initialization/routing/hasItemAccessByStateName';

export const StorefrontsMenu = () => {
  const isInstashopEnabled = hasItemAccessByStateName('instashop');
  const isGalleriesEnabled = useSelector(getIsGalleriesEnabled);
  const isEmailBuilderEnabled = useSelector(getIsEmailBuilderEnabled);
  const isGoogleAssetFeedsEnabled = useSelector(getIsGoogleAssetFeedsEnabled);
  const isLike2BuyV2Enabled = useSelector(getIsLike2BuyV2Enabled);
  const isLike2BuyEnabled = useSelector(getIsLike2BuyEnabled);
  const isLike2BuyTiktokEnabled = useSelector(getIsLike2BuyTiktokEnabled);
  const isInstagramShopsUgcEnabled = useSelector(getIsInstagramShopsUgcEnabled);
  const isTributarySelfServeEnabled = useSelector(
    getIsTributarySelfServeEnabled,
  );
  const isInstagramAdsContentAllowlistingEnabled = useSelector(
    getIsInstagramAdsContentAllowlistingEnabled,
  );
  const isPromotedPostsEnabled = useSelector(getIsPromotedPostsAccessEnabled);
  const isTiktokSparkAdsEnabled = useSelector(getIsTiktokSparkAdsEnabled);
  const isRevealPageEnabled = useSelector(getIsRevealInsightsFeatureEnabled);
  const isInstashopFeatureEnabled = useSelector(getIsInstashopFeatureEnabled);
  const isBazaarvoiceNetworkFeatureEnabled = useSelector(
    getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  );
  const isCuralateEmployee = useSelector(getIsCuralateEmployee);

  const isLike2BuyInstagramEnabled =
    isLike2BuyEnabled && (!isLike2BuyV2Enabled || isCuralateEmployee);

  const isSocialVisible =
    isLike2BuyEnabled ||
    isLike2BuyV2Enabled ||
    isLike2BuyTiktokEnabled ||
    isTributarySelfServeEnabled ||
    isInstagramShopsUgcEnabled;

  const getSocialState = () => {
    if (isLike2BuyInstagramEnabled) {
      return 'instagramAccount.like2buy';
    } else if (isLike2BuyTiktokEnabled) {
      return 'tiktok.like2buy';
    } else if (isLike2BuyV2Enabled) {
      return 'instagramAccount.l2b-v2';
    } else if (isInstagramShopsUgcEnabled) {
      return 'instagramAccount.instagram-shops';
    }
    return 'showroom';
  };

  const isPaidSocialVisible =
    isInstagramAdsContentAllowlistingEnabled ||
    isPromotedPostsEnabled ||
    isTiktokSparkAdsEnabled;

  const getPaidSocialState = () => {
    if (isInstagramAdsContentAllowlistingEnabled) {
      return 'meta-ads';
    } else if (isPromotedPostsEnabled) {
      return 'instagramAccount.promotedPostMetrics';
    }
    return 'spark-ads';
  };

  const isVisualSyndicationVisible =
    isBazaarvoiceNetworkFeatureEnabled && !isInstashopFeatureEnabled;

  return (
    <NavDropdown>
      {isSocialVisible && (
        <NavDropdownItem
          primary='Social'
          state={getSocialState()}
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.SOCIAL_DROPDOWN}
        />
      )}
      {isGalleriesEnabled && (
        <NavDropdownItem
          primary='Galleries'
          state='galleries'
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.ECOMMERCE_DROPDOWN}
        />
      )}
      {isVisualSyndicationVisible && (
        <NavDropdownItem
          primary='Visual Syndication Network'
          state='syndicationNetwork'
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.VSN_DROPDOWN}
        />
      )}
      {isInstashopEnabled && !isGalleriesEnabled && (
        <NavDropdownItem
          primary='InstaShop'
          state='instashop'
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.INSTASHOP_DROPDOWN}
        />
      )}
      {isPaidSocialVisible && (
        <NavDropdownItem
          primary='Paid Social'
          state={getPaidSocialState()}
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.PAID_SOCIAL_DROPDOWN}
        />
      )}
      {isEmailBuilderEnabled && (
        <NavDropdownItem
          primary='Email'
          state='email-storefronts'
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.EMAIL_DROPDOWN}
        />
      )}
      {isRevealPageEnabled && (
        <NavDropdownItem
          primary='Reveal'
          state='reveal'
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.REVEAL_DROPDOWN}
        />
      )}
      {isGoogleAssetFeedsEnabled && (
        <NavDropdownItem
          primary='Google'
          state='tagging'
          trackingId={NAV_DROPDOWN_ITEMS_TRACKING_ID.GOOGLE_DROPDOWN}
        />
      )}
    </NavDropdown>
  );
};
